import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { DsButton, DsCard, DsIcon } from "../design-system/components";
import HomeHighlightsPanel from "../components/Admin/HomeHighlightsPanel";
import { usePermissions } from "../rbac/PermissionsContext";
import { getAuthJson, setAuthItem } from "../utils/authStorage";
import { roundLeaveDaysForDisplay } from "../lib/leaveBalanceDisplay";
import { deskLatestActivityMs } from "../utils/deskListOrder";
import {
  ATTENDANCE_PUNCH_UPDATED_EVENT,
  getLocalDayKey,
  hydrateEmployeeWorklogFromAttendance,
  readEmployeeWorklog,
  workedMsFromPunchRows,
  workedMsFromSessions,
} from "../lib/attendancePunchStorage";
import {
  buildMonthCells,
  displayNameFromUser,
  enrichAttendanceWithPolicies,
  formatDurationMs,
  formatPunchTime,
  greetingForNow,
  monthTitle,
  pickUpcomingHolidays,
} from "../lib/essHomeHelpers";
import { syncAttendancePoliciesCache } from "../lib/syncAttendancePoliciesCache";
import { allowEssQuickPanelScreen } from "../lib/essQuickPanelRequestAccess";
import {
  attendanceService,
  attendanceStructureService,
  authService,
  employeeService,
  homeService,
} from "../services/api";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const SHIFT_MS = 8 * 60 * 60 * 1000;

function SectionLink({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ds-caption font-semibold text-[hsl(var(--ds-primary))] hover:underline"
    >
      {children}
    </button>
  );
}

function StatusBadge({ tone = "ok", children }) {
  const tones = {
    ok: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    blue: "bg-sky-50 text-sky-700",
  };
  return (
    <span
      className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${
        tones[tone] || tones.ok
      }`}
    >
      {children}
    </span>
  );
}

const EmployeeHome = memo(function EmployeeHome() {
  const navigate = useNavigate();
  const { hasScreenAccess, isAdmin } = usePermissions();

  const canSeeBirthdays = isAdmin || hasScreenAccess("home", "birthdays", "view");
  const canSeeAnniversaries =
    isAdmin || hasScreenAccess("home", "work_anniversaries", "view");
  const canSeeNewJoinees = isAdmin || hasScreenAccess("home", "new_joinees", "view");
  const canSeeGlance = isAdmin || hasScreenAccess("home", "glance", "view");
  const canSeeCalendar =
    isAdmin || hasScreenAccess("home", "ess_attendance_calendar", "view");
  const canSeeLeaveBalance =
    isAdmin || hasScreenAccess("home", "ess_leave_balance", "view");
  const canSeeHolidays =
    isAdmin || hasScreenAccess("home", "ess_upcoming_holidays", "view");
  const canSeeMyRequests =
    isAdmin || hasScreenAccess("home", "ess_my_requests", "view");
  const canSeeTeamAway = isAdmin || hasScreenAccess("home", "ess_team_away", "view");
  const canSeePayslipTax =
    isAdmin || hasScreenAccess("home", "ess_payslip_tax", "view");

  const showHighlights = canSeeBirthdays || canSeeAnniversaries || canSeeNewJoinees;
  const allowQuick = (screenKey) =>
    allowEssQuickPanelScreen(hasScreenAccess, isAdmin, screenKey);

  const [companyData, setCompanyData] = useState(() => getAuthJson("companyData"));
  const [user, setUser] = useState(() => getAuthJson("currentUser"));
  const [employee, setEmployee] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);

  const now = new Date();
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [calYear, setCalYear] = useState(now.getFullYear());
  const [attendanceByDay, setAttendanceByDay] = useState({});
  const [policies, setPolicies] = useState({
    weeklyOffPolicies: [],
    employeeWeeklyOffMappings: {},
    holidaysData: [],
    holidayPolicies: [],
    employeeHolidayMappings: {},
  });
  const [leaveTiles, setLeaveTiles] = useState([]);
  const [leaveTotal, setLeaveTotal] = useState(null);
  const [myRequests, setMyRequests] = useState([]);
  const [pendingMineCount, setPendingMineCount] = useState(0);
  const [teamAway, setTeamAway] = useState([]);
  const [payslipHint, setPayslipHint] = useState(null);
  const [punchTick, setPunchTick] = useState(0);
  const [livePunches, setLivePunches] = useState([]);
  const [liveSessions, setLiveSessions] = useState([]);
  const [liveOpen, setLiveOpen] = useState(false);
  const [loadingCore, setLoadingCore] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const onCompany = (e) => setCompanyData(e.detail);
    window.addEventListener("companyDataUpdated", onCompany);
    return () => window.removeEventListener("companyDataUpdated", onCompany);
  }, []);

  const loadLiveSessionHours = useCallback(async (empId) => {
    if (!empId) {
      setLivePunches([]);
      setLiveSessions([]);
      setLiveOpen(false);
      return;
    }
    try {
      const res = await attendanceService.getCurrentAttendanceSession();
      if (!res?.success || !res.data) {
        setLivePunches([]);
        setLiveSessions([]);
        setLiveOpen(false);
        return;
      }
      const session = res.data;
      const punches = Array.isArray(session.punches) ? session.punches : [];
      const sessions = Array.isArray(session.sessions) ? session.sessions : [];
      setLivePunches(punches);
      setLiveSessions(sessions);
      setLiveOpen(session.open === true);
      // Prefer punch pairs so worklog is not FILO-only after checkout.
      if (punches.length > 0 || sessions.length > 0) {
        hydrateEmployeeWorklogFromAttendance(empId, {
          date: getLocalDayKey(),
          checkIn: session.checkIn,
          checkOut: session.checkOut,
          punches,
        });
      }
    } catch {
      setLivePunches([]);
      setLiveSessions([]);
      setLiveOpen(false);
    }
  }, []);

  useEffect(() => {
    const onPunch = () => {
      setPunchTick((n) => n + 1);
      if (employeeId) loadLiveSessionHours(employeeId);
    };
    window.addEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
    // Match navbar live Work Hours tick while a session can still accumulate.
    const id = window.setInterval(() => setPunchTick((n) => n + 1), 1000);
    return () => {
      window.removeEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
      window.clearInterval(id);
    };
  }, [employeeId, loadLiveSessionHours]);

  const loadIdentity = useCallback(async () => {
    try {
      const me = await authService.getCurrentUser();
      const u = me?.data || getAuthJson("currentUser");
      setUser(u || null);
      const empId = u?.employeeId;
      if (!empId) {
        setEmployee(null);
        setEmployeeId(null);
        return null;
      }
      setEmployeeId(empId);
      try {
        setAuthItem("employeeId", String(empId));
      } catch {
        /* ignore */
      }
      const empRes = await employeeService.getEmployee(empId);
      const emp = empRes?.data || null;
      setEmployee(emp);
      return { user: u, employee: emp, employeeId: empId };
    } catch {
      const u = getAuthJson("currentUser");
      setUser(u);
      setEmployeeId(u?.employeeId || null);
      return { user: u, employee: null, employeeId: u?.employeeId || null };
    }
  }, []);

  const loadPolicies = useCallback(async () => {
    try {
      const [woResp, woMapResp, holidaysResp, hpResp, hpMapResp] = await Promise.all([
        attendanceStructureService.getWeeklyOffPolicies().catch(() => ({ data: [] })),
        attendanceStructureService
          .getEmployeeWeeklyOffPolicies()
          .catch(() => ({ data: {} })),
        attendanceStructureService.getHolidays().catch(() => ({ data: [] })),
        attendanceStructureService.getHolidayPolicies().catch(() => ({ data: [] })),
        attendanceStructureService
          .getEmployeeHolidayPolicies()
          .catch(() => ({ data: {} })),
      ]);
      setPolicies({
        weeklyOffPolicies: woResp?.data || [],
        employeeWeeklyOffMappings: woMapResp?.data || {},
        holidaysData: holidaysResp?.data || [],
        holidayPolicies: hpResp?.data || [],
        employeeHolidayMappings: hpMapResp?.data || {},
      });
    } catch {
      /* keep previous */
    }
  }, []);

  const loadAttendanceMonth = useCallback(async (emp) => {
    const empId = emp?.id ?? emp?.employeeId ?? employeeId;
    if (!empId) {
      setAttendanceByDay({});
      return;
    }
    try {
      const response = await attendanceService.getMyAttendanceHistory(
        calMonth + 1,
        calYear
      );
      const map = {};
      if (response?.success && Array.isArray(response.data)) {
        response.data.forEach((record) => {
          const raw = record.date;
          if (!raw || typeof raw !== "string") return;
          const parts = raw.trim().split(/[-/]/);
          if (parts.length < 3) return;
          const day = parseInt(parts[2], 10);
          if (Number.isNaN(day) || day < 1 || day > 31) return;
          map[day] = record.status || "-";
        });
        // Do not FILO-hydrate today from month history (checkIn/checkOut only).
        // Today's Hours use current-session punch pairs (same as navbar).
      }
      setAttendanceByDay(map);
    } catch {
      setAttendanceByDay({});
    }
  }, [calMonth, calYear, employeeId]);

  const loadLeaveBalances = useCallback(async (empId) => {
    if (!empId) {
      setLeaveTiles([]);
      setLeaveTotal(null);
      return;
    }
    try {
      const typesRes = await attendanceStructureService.getEmployeeLeaveTypes(empId);
      const types = Array.isArray(typesRes?.data) ? typesRes.data : [];
      const year = new Date().getFullYear();
      const month = new Date().getMonth() + 1;
      const top = types.slice(0, 4);
      const tiles = await Promise.all(
        top.map(async (lt) => {
          const id = lt.id ?? lt.leaveTypeId;
          try {
            const data = await attendanceStructureService.getLeaveBalance(empId, year, id);
            const months = Array.isArray(data)
              ? data
              : data?.months ?? data?.list ?? [];
            const row =
              months.find((m) => Number(m.month) === month) ||
              months[months.length - 1] ||
              null;
            const closing = Number(row?.closingBalance ?? 0);
            const rounding = data?.policy?.balanceRounding ?? "full_day";
            const code = lt.code || lt.leaveCode || lt.shortName || "LV";
            const name = lt.name || lt.leaveName || lt.typeName || "";
            const policy = data?.policy;
            let sub = closing > 0 ? "Available" : "No balance";
            if (closing <= 0 && policy) {
              sub = [
                policy.creditFrequency,
                policy.creditMethod,
                policy.creditOn,
              ]
                .filter(Boolean)
                .join(" · ");
            }
            return {
              id,
              code,
              label: name ? `${code} · ${name}` : code,
              balance: roundLeaveDaysForDisplay(closing, rounding),
              raw: closing,
              sub: sub || "No balance",
              creditHint: data?.creditHint || null,
              policy,
            };
          } catch {
            return {
              id,
              code: lt.code || "LV",
              label: lt.name || lt.code || "Leave",
              balance: "—",
              raw: 0,
              sub: "Unavailable",
            };
          }
        })
      );
      setLeaveTiles(tiles);
      const sum = tiles.reduce(
        (acc, t) => acc + (Number.isFinite(t.raw) ? t.raw : 0),
        0
      );
      setLeaveTotal(roundLeaveDaysForDisplay(sum, "half_day"));
    } catch {
      setLeaveTiles([]);
      setLeaveTotal(null);
    }
  }, []);

  const loadMyRequests = useCallback(async (empId) => {
    if (!empId) {
      setMyRequests([]);
      setPendingMineCount(0);
      return;
    }
    try {
      const mineRes = await attendanceStructureService.getLeaveRequests({
        employeeId: empId,
        limit: 20,
      });
      const rows = Array.isArray(mineRes?.data) ? mineRes.data : [];
      const sorted = [...rows].sort(
        (a, b) => deskLatestActivityMs(b) - deskLatestActivityMs(a)
      );
      const pending = sorted.filter((r) =>
        /pending/i.test(String(r.status || r.requestStatus || ""))
      ).length;
      setPendingMineCount(pending);
      setMyRequests(
        sorted.slice(0, 3).map((r) => {
          const status = String(r.status || r.requestStatus || "—");
          const leaveName =
            r.leaveTypeName || r.leaveType?.name || r.type || r.requestType || "Request";
          const from = String(r.fromDate || r.date || "").slice(0, 10);
          let dateLabel = "";
          if (from) {
            const d = new Date(`${from}T12:00:00`);
            dateLabel = Number.isNaN(d.getTime())
              ? from
              : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
          }
          return {
            id: r.id,
            label: dateLabel ? `${leaveName} · ${dateLabel}` : leaveName,
            status,
            pending: /pending/i.test(status),
            approved: /approv/i.test(status),
          };
        })
      );
    } catch {
      setMyRequests([]);
      setPendingMineCount(0);
    }
  }, []);

  const loadTeamAway = useCallback(async () => {
    try {
      const resp = await employeeService.getMyReportees();
      const list = resp?.success && Array.isArray(resp.data) ? resp.data : [];
      const away = list
        .filter((e) => {
          const st = String(e.status || e.employmentStatus || "").toLowerCase();
          return st.includes("leave") || st.includes("wfh") || st.includes("remote");
        })
        .slice(0, 3)
        .map((e) => ({
          id: e.id,
          label: `${e.name || e.employeeName || e.empCode || "Teammate"}${
            e.leaveTypeCode ? ` · ${e.leaveTypeCode}` : ""
          }`,
          badge: String(e.status || "Away"),
        }));
      setTeamAway(away);
    } catch {
      setTeamAway([]);
    }
  }, []);

  const loadPayslipHint = useCallback(async () => {
    if (companyData?.module_payroll !== 1) {
      setPayslipHint(null);
      return;
    }
    try {
      const year = new Date().getFullYear();
      const res = await employeeService.getMyPayslipsForYear(year);
      const rows = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      if (!rows.length) {
        setPayslipHint({ label: `No payslip yet · ${year}`, tone: "amber" });
        return;
      }
      const latest = rows[0];
      const my =
        latest.monthYear ||
        latest.payMonth ||
        latest.period ||
        latest.monthLabel ||
        "Latest";
      setPayslipHint({ label: `Latest payslip · ${my}`, tone: "ok" });
    } catch {
      setPayslipHint({ label: "Payslips", tone: "ok" });
    }
  }, [companyData?.module_payroll]);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const idn = await loadIdentity();
      await Promise.all([
        syncAttendancePoliciesCache(),
        loadPolicies(),
        loadAttendanceMonth(idn?.employee),
        loadLeaveBalances(idn?.employeeId),
        loadMyRequests(idn?.employeeId),
        loadTeamAway(),
        loadPayslipHint(),
        loadLiveSessionHours(idn?.employeeId),
        homeService.getHighlights({ days: 7 }).catch(() => null),
      ]);
    } finally {
      setLoadingCore(false);
      setRefreshing(false);
      setPunchTick((n) => n + 1);
    }
  }, [
    loadIdentity,
    loadPolicies,
    loadAttendanceMonth,
    loadLeaveBalances,
    loadMyRequests,
    loadTeamAway,
    loadPayslipHint,
    loadLiveSessionHours,
  ]);

  useEffect(() => {
    refreshAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount once

  useEffect(() => {
    if (!employee) return;
    loadAttendanceMonth(employee);
  }, [calMonth, calYear, employee, loadAttendanceMonth]);

  const enrichedAttendance = useMemo(
    () =>
      enrichAttendanceWithPolicies({
        attendanceByDay,
        year: calYear,
        monthIndex: calMonth,
        employee,
        ...policies,
      }),
    [attendanceByDay, calYear, calMonth, employee, policies]
  );

  const calCells = useMemo(
    () => buildMonthCells(calYear, calMonth, enrichedAttendance),
    [calYear, calMonth, enrichedAttendance]
  );

  const upcomingHolidays = useMemo(
    () => pickUpcomingHolidays(policies.holidaysData, { limit: 3 }),
    [policies.holidaysData]
  );

  const worklog = useMemo(() => {
    void punchTick;
    if (!employeeId) return null;
    return readEmployeeWorklog(employeeId, getLocalDayKey());
  }, [employeeId, punchTick]);

  const lastPunchMs =
    livePunches.length > 0
      ? new Date(
          livePunches[livePunches.length - 1].punchedAt ||
            livePunches[livePunches.length - 1].punched_at
        ).getTime()
      : worklog?.inMs;
  const punchInLabel = formatPunchTime(lastPunchMs);
  const attendanceStatus =
    livePunches.length > 0 || liveSessions.length > 0
      ? liveOpen
        ? "In"
        : "Out"
      : worklog?.open
        ? "In"
        : worklog?.inMs
          ? worklog?.outMs
            ? "Out"
            : "In"
          : "—";
  // Same math as navbar Work Hours: sum of IN→OUT pairs (not Last OUT − First IN).
  void punchTick;
  const nowMs = Date.now();
  const todayKey = getLocalDayKey(nowMs);
  const punchMs =
    livePunches.length > 0 ? workedMsFromPunchRows(livePunches, nowMs) : null;
  const sessionMs =
    liveSessions.length > 0
      ? workedMsFromSessions(
          {
            sessions: liveSessions,
            open: liveOpen,
            inMs: liveSessions[0]?.inMs,
            outMs: liveOpen
              ? null
              : liveSessions[liveSessions.length - 1]?.outMs ?? null,
          },
          nowMs
        )
      : null;
  const worklogMs =
    worklog && getLocalDayKey(worklog.inMs) === todayKey
      ? workedMsFromSessions(worklog, nowMs)
      : 0;
  const hoursTodayMs = punchMs != null ? punchMs : sessionMs != null ? sessionMs : worklogMs;
  const hoursToday = formatDurationMs(hoursTodayMs);
  const shiftLabel = formatDurationMs(SHIFT_MS);

  const firstName = displayNameFromUser(user);
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  const quickActions = [
    allowQuick("leave_request") && {
      label: "Apply Leave",
      to: "/employee/attendance",
    },
    allowQuick("regularization_checks") && {
      label: "Regularise",
      to: "/employee/attendance",
    },
    companyData?.module_payroll === 1 && {
      label: "My Payslips",
      to: "/employee/payroll?tab=payslips",
    },
    allowQuick("tax_declaration") && {
      label: "Tax Planning",
      to: "/employee/payroll",
    },
  ].filter(Boolean);

  const shiftChip =
    employee?.shiftName ||
    employee?.defaultShiftName ||
    employee?.shift?.name ||
    null;

  return (
    <div className="min-h-full w-full bg-[hsl(var(--ds-bg))] p-4 lg:p-6">
      <div className="w-full max-w-none space-y-4">
        {/* Welcome */}
        <DsCard className="!p-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h1 className="ds-h2 text-[hsl(var(--ds-fg))]">
                {greetingForNow()}, {firstName}
              </h1>
              <p className="ds-body mt-1 max-w-xl text-[hsl(var(--ds-muted-fg))]">
                Your day at a glance — attendance, leave, holidays, and requests.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-2.5 py-1 text-[11px] font-semibold text-[hsl(var(--ds-fg))]">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      worklog?.open
                        ? "bg-[hsl(var(--ds-success))]"
                        : "bg-[hsl(var(--ds-muted-fg))]"
                    }`}
                  />
                  {punchInLabel
                    ? `Checked in · ${punchInLabel}`
                    : "Not checked in yet"}
                </span>
                {shiftChip ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-2.5 py-1 text-[11px] font-semibold text-[hsl(var(--ds-fg))]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--ds-warning))]" />
                    Shift: {shiftChip}
                  </span>
                ) : null}
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-2.5 py-1 text-[11px] font-semibold text-[hsl(var(--ds-fg))]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--ds-primary))]" />
                  {todayLabel}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {quickActions.map((a) => (
                <DsButton
                  key={a.label}
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(a.to)}
                >
                  {a.label}
                </DsButton>
              ))}
            </div>
          </div>
        </DsCard>

        {showHighlights ? (
          <HomeHighlightsPanel
            darkMode={false}
            showBirthdays={canSeeBirthdays}
            showAnniversaries={canSeeAnniversaries}
            showNewJoinees={canSeeNewJoinees}
            onViewEmployee={() => navigate("/employee/profile")}
          />
        ) : null}

        {/* Personal glance */}
        {canSeeGlance ? (
          <DsCard className="!p-5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="ds-h4 text-[hsl(var(--ds-fg))]">Today at a Glance</h2>
              <button
                type="button"
                onClick={() => refreshAll()}
                disabled={refreshing}
                className="inline-flex items-center gap-1.5 ds-caption font-semibold text-[hsl(var(--ds-primary))] hover:underline disabled:opacity-50"
              >
                <DsIcon icon={RefreshCw} size="sm" className={refreshing ? "animate-spin" : ""} />
                Refresh · live
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-4">
                <p className="ds-caption uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                  Attendance today
                </p>
                <p
                  className={`mt-1 text-2xl font-bold ${
                    attendanceStatus === "In"
                      ? "text-[hsl(var(--ds-success))]"
                      : "text-[hsl(var(--ds-fg))]"
                  }`}
                >
                  {loadingCore ? "…" : attendanceStatus}
                </p>
                <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
                  {punchInLabel
                    ? `Last punch ${punchInLabel}`
                    : "No punch recorded yet"}
                </p>
              </div>
              <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-4">
                <p className="ds-caption uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                  Hours today
                </p>
                <p className="mt-1 text-2xl font-bold tabular-nums text-[hsl(var(--ds-fg))]">
                  {loadingCore ? "…" : hoursToday}
                </p>
                <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
                  Of {shiftLabel} shift
                </p>
              </div>
              <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-4">
                <p className="ds-caption uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                  Leave balance
                </p>
                <p className="mt-1 text-2xl font-bold tabular-nums text-[hsl(var(--ds-fg))]">
                  {leaveTotal ?? "—"}
                </p>
                <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
                  Days available (all types)
                </p>
              </div>
              <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-4">
                <p className="ds-caption uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                  My pending
                </p>
                <p className="mt-1 text-2xl font-bold tabular-nums text-[hsl(var(--ds-fg))]">
                  {String(pendingMineCount).padStart(2, "0")}
                </p>
                <p className="mt-1 text-xs text-[hsl(var(--ds-muted-fg))]">
                  Leave / requests awaiting approval
                </p>
              </div>
            </div>
          </DsCard>
        ) : null}

        {/* Calendar + Leave — matched height row */}
        {(canSeeCalendar || canSeeLeaveBalance) && (
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
            {canSeeCalendar ? (
              <DsCard className="!p-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h2 className="ds-h4 text-[hsl(var(--ds-fg))]">Attendance calendar</h2>
                  <SectionLink onClick={() => navigate("/employee/attendance")}>
                    Open full calendar
                  </SectionLink>
                </div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-[13px] font-semibold text-[hsl(var(--ds-fg))]">
                    {monthTitle(calYear, calMonth)}
                  </h3>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      aria-label="Previous month"
                      className="flex h-[26px] w-[26px] items-center justify-center rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))]"
                      onClick={() => {
                        if (calMonth === 0) {
                          setCalMonth(11);
                          setCalYear((y) => y - 1);
                        } else setCalMonth((m) => m - 1);
                      }}
                    >
                      <DsIcon icon={ChevronLeft} size="sm" />
                    </button>
                    <button
                      type="button"
                      aria-label="Next month"
                      className="flex h-[26px] w-[26px] items-center justify-center rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))]"
                      onClick={() => {
                        if (calMonth === 11) {
                          setCalMonth(0);
                          setCalYear((y) => y + 1);
                        } else setCalMonth((m) => m + 1);
                      }}
                    >
                      <DsIcon icon={ChevronRight} size="sm" />
                    </button>
                  </div>
                </div>
                <div className="mb-0.5 grid grid-cols-7 gap-0.5">
                  {WEEKDAYS.map((d) => (
                    <span
                      key={d}
                      className="py-0.5 text-center text-[9px] font-semibold text-[hsl(var(--ds-muted-fg))]"
                    >
                      {d}
                    </span>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-0.5" aria-label="Month calendar">
                  {calCells.map((cell) => (
                    <div
                      key={cell.key}
                      className={[
                        "flex h-6 flex-col items-center justify-center gap-px rounded-md text-[10px] font-semibold",
                        cell.mute
                          ? "text-[hsl(var(--ds-border))]"
                          : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]",
                        cell.isToday
                          ? "border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))]/10 text-[hsl(var(--ds-primary))]"
                          : "border border-transparent",
                      ].join(" ")}
                    >
                      <span>{cell.day}</span>
                      {!cell.mute && cell.kind ? (
                        <span
                          className={[
                            "h-1 w-1 rounded-full",
                            cell.kind === "present" && "bg-[hsl(var(--ds-success))]",
                            cell.kind === "leave" && "bg-[hsl(var(--ds-info))]",
                            cell.kind === "holiday" && "bg-[hsl(var(--ds-warning))]",
                            cell.kind === "absent" && "bg-[hsl(var(--ds-danger))]",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-[9px] font-medium text-[hsl(var(--ds-muted-fg))]">
                  <span className="inline-flex items-center gap-1">
                    <i className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--ds-success))]" />
                    Present
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--ds-info))]" />
                    Leave
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--ds-warning))]" />
                    Holiday / WO
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--ds-danger))]" />
                    Absent / LOP
                  </span>
                  <span>Border = today</span>
                </div>
              </DsCard>
            ) : null}

            {canSeeLeaveBalance ? (
              <DsCard className="!p-4">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h2 className="ds-h4 text-[hsl(var(--ds-fg))]">Leave balance</h2>
                  <SectionLink onClick={() => navigate("/employee/attendance")}>
                    Apply leave
                  </SectionLink>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  {(leaveTiles.length ? leaveTiles : [
                    { id: "e1", label: "—", balance: "—", sub: "Loading…" },
                    { id: "e2", label: "—", balance: "—", sub: "…" },
                    { id: "e3", label: "—", balance: "—", sub: "…" },
                    { id: "e4", label: "—", balance: "—", sub: "…" },
                  ]).map((t) => (
                    <div
                      key={t.id}
                      className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] p-3"
                    >
                      <div className="text-[11px] font-bold text-[hsl(var(--ds-primary))]">
                        {t.label}
                      </div>
                      <div className="mt-1 text-xl font-bold tabular-nums text-[hsl(var(--ds-fg))]">
                        {t.balance}
                      </div>
                      <div
                        className="mt-0.5 text-[10px] text-[hsl(var(--ds-muted-fg))] line-clamp-2"
                        title={t.creditHint || t.sub || ""}
                      >
                        {t.sub}
                      </div>
                    </div>
                  ))}
                </div>
              </DsCard>
            ) : null}
          </div>
        )}

        {/* Holidays */}
        {canSeeHolidays ? (
          <DsCard className="!p-4">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="ds-h4 text-[hsl(var(--ds-fg))]">Upcoming holidays</h2>
              <SectionLink onClick={() => navigate("/employee/attendance")}>
                View all
              </SectionLink>
            </div>
            {upcomingHolidays.length === 0 ? (
              <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
                No upcoming holidays in the calendar.
              </p>
            ) : (
              <ul className="grid grid-cols-1 gap-x-5 md:grid-cols-3">
                {upcomingHolidays.map((h) => (
                  <li
                    key={`${h.id}-${h.date}`}
                    className="flex items-start justify-between gap-3 border-b border-[hsl(var(--ds-border))] py-2.5 last:border-b-0 md:border-b-0 md:py-1"
                  >
                    <div>
                      <div className="text-[13px] font-semibold text-[hsl(var(--ds-fg))]">
                        {h.name}
                      </div>
                      <div className="mt-0.5 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                        {h.meta}
                      </div>
                    </div>
                    <div className="shrink-0 rounded-lg bg-[hsl(var(--ds-primary))]/10 px-2 py-1.5 text-right text-[11px] font-semibold text-[hsl(var(--ds-primary))]">
                      {h.dayLabel}
                      <br />
                      {h.weekday} ·{" "}
                      {h.daysUntil === 0 ? "today" : `in ${h.daysUntil}d`}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </DsCard>
        ) : null}

        {/* Bottom row */}
        {(canSeeMyRequests || canSeeTeamAway || canSeePayslipTax) && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {canSeeMyRequests ? (
              <DsCard className="!p-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h2 className="ds-h4 text-[hsl(var(--ds-fg))]">My requests</h2>
                  <SectionLink onClick={() => navigate("/employee/attendance")}>
                    All
                  </SectionLink>
                </div>
                {myRequests.length === 0 ? (
                  <p className="text-xs text-[hsl(var(--ds-muted-fg))]">No recent requests</p>
                ) : (
                  myRequests.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between gap-2 border-b border-[hsl(var(--ds-border))] py-2 text-xs last:border-b-0"
                    >
                      <span className="truncate text-[hsl(var(--ds-fg))]">{r.label}</span>
                      <StatusBadge tone={r.pending ? "amber" : r.approved ? "ok" : "blue"}>
                        {r.status}
                      </StatusBadge>
                    </div>
                  ))
                )}
              </DsCard>
            ) : null}

            {canSeeTeamAway ? (
              <DsCard className="!p-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h2 className="ds-h4 text-[hsl(var(--ds-fg))]">Team away today</h2>
                  <SectionLink onClick={() => navigate("/employee/my-team")}>
                    My team
                  </SectionLink>
                </div>
                {teamAway.length === 0 ? (
                  <div className="flex items-center justify-between gap-2 py-2 text-xs">
                    <span className="text-[hsl(var(--ds-muted-fg))]">
                      No reportees away (or no team)
                    </span>
                    <StatusBadge>OK</StatusBadge>
                  </div>
                ) : (
                  teamAway.map((t) => (
                    <div
                      key={t.id}
                      className="flex items-center justify-between gap-2 border-b border-[hsl(var(--ds-border))] py-2 text-xs last:border-b-0"
                    >
                      <span className="truncate text-[hsl(var(--ds-fg))]">{t.label}</span>
                      <StatusBadge tone="blue">{t.badge}</StatusBadge>
                    </div>
                  ))
                )}
              </DsCard>
            ) : null}

            {canSeePayslipTax ? (
              <DsCard className="!p-4">
                <div className="mb-2 flex items-center justify-between gap-2">
                  <h2 className="ds-h4 text-[hsl(var(--ds-fg))]">Payslip &amp; tax</h2>
                  <SectionLink onClick={() => navigate("/employee/payroll")}>
                    Payroll
                  </SectionLink>
                </div>
                <div className="flex items-center justify-between gap-2 border-b border-[hsl(var(--ds-border))] py-2 text-xs">
                  <span className="truncate text-[hsl(var(--ds-fg))]">
                    {payslipHint?.label || "Payslips"}
                  </span>
                  <StatusBadge tone={payslipHint?.tone === "amber" ? "amber" : "ok"}>
                    {payslipHint?.tone === "amber" ? "Action" : "Ready"}
                  </StatusBadge>
                </div>
                <div className="flex items-center justify-between gap-2 border-b border-[hsl(var(--ds-border))] py-2 text-xs">
                  <span className="text-[hsl(var(--ds-fg))]">Tax Planning</span>
                  <StatusBadge tone="amber">Open</StatusBadge>
                </div>
                <div className="flex items-center justify-between gap-2 py-2 text-xs">
                  <span className="text-[hsl(var(--ds-fg))]">Form 16</span>
                  <StatusBadge>Payroll</StatusBadge>
                </div>
              </DsCard>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
});

export default EmployeeHome;
